import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserCheck, Loader2, ArrowRightLeft } from "lucide-react";
import api, { formatErr } from "../lib/api";
import { BRAND_COLORS, STATUS_COLORS, withAlpha } from "../lib/bookingStatus";

/**
 * Feb 2026 · Property → Concierge-Zuordnung (Option b).
 *
 * Zeigt den aktuell zugewiesenen Concierge für ein Objekt und lässt den
 * Admin per Dropdown ändern. Ein leerer Wert („—") entfernt die Zuweisung
 * (Backend erkennt den Empty-String und speichert `null`).
 *
 * Concierge-Quelle: Merge aus `/admin/allowed-emails` (Zugänge-Liste) und
 * `/team/members` (echte User-IDs). Ohne user_id ist eine Zuweisung nicht
 * möglich (Filter würde ins Leere greifen) — solche Einträge tauchen im
 * Dropdown nicht auf.
 */
export default function PropertyConciergeAssign({ property, onSaved }) {
  const [concierges, setConcierges] = useState(null);
  const [saving, setSaving] = useState(false);
  const [value, setValue] = useState(property.assigned_concierge_id || "");
  // Feb 2026 · 10 Feb 2026 · Cascade-Confirm-Modal
  const [cascade, setCascade] = useState(null); // {preview, next, oldName, newName} | null

  useEffect(() => {
    setValue(property.assigned_concierge_id || "");
  }, [property.assigned_concierge_id]);

  useEffect(() => {
    (async () => {
      try {
        // Feb 2026 · Directive „Admin-eigene Concierge-Ebene": Admins
        // erscheinen zusätzlich zu Concierges als wählbare Verantwortliche.
        // Quelle: `/team/members` liefert alle eingeloggten Admin+Concierge-
        // User mit garantierter `id`.  Ergänzt werden nur eingeladene, noch
        // nicht eingeloggte Concierges aus `/admin/allowed-emails` — für
        // Admins gibt es keine „eingeladen"-Halbwelt.
        const [members, allowed] = await Promise.all([
          api.get("/team/members").catch(() => ({ data: [] })),
          api.get("/admin/allowed-emails").catch(() => ({ data: [] })),
        ]);
        const teamMembers = (members.data || []).filter((m) => ["admin", "concierge"].includes(m.role));
        const seenEmails = new Set(teamMembers.map((m) => (m.email || "").toLowerCase()));
        const invitedConcierges = (allowed.data || [])
          .filter((r) => r.role === "concierge" && !seenEmails.has((r.email || "").toLowerCase()))
          .map((r) => ({
            id: r.user_id || null,
            email: r.email,
            name: r.name || r.email,
            role: "concierge",
          }))
          .filter((r) => r.id); // ohne echte user_id nicht wählbar
        const merged = [...teamMembers, ...invitedConcierges]
          .map((m) => ({
            user_id: m.id,
            email: m.email,
            name: m.name || m.email,
            role: m.role,
          }))
          .sort((a, b) => {
            // Admins zuerst (Owner-Präsenz signalisieren), dann alphabetisch.
            if (a.role !== b.role) return a.role === "admin" ? -1 : 1;
            return (a.name || "").localeCompare(b.name || "");
          });
        setConcierges(merged);
      } catch { setConcierges([]); }
    })();
  }, []);

  const nameOf = (id) => {
    if (!id) return "—";
    const hit = (concierges || []).find((c) => c.user_id === id);
    return hit?.name || "unbekannt";
  };

  const commit = async (next, doCascade) => {
    setSaving(true);
    try {
      const { data } = await api.post(`/properties/${property.id}/reassign-concierge`, {
        new_concierge_id: next || "",
        cascade: !!doCascade,
      });
      onSaved?.(data.property);
      setValue(next);
      const m = data.moved || {};
      const parts = [
        m.bookings ? `${m.bookings} Buchung${m.bookings === 1 ? "" : "en"}` : "",
        m.requests ? `${m.requests} Aufgabe${m.requests === 1 ? "" : "n"}` : "",
        m.projects ? `${m.projects} Projekt${m.projects === 1 ? "" : "e"}` : "",
        m.calendar_events ? `${m.calendar_events} Termin${m.calendar_events === 1 ? "" : "e"}` : "",
      ].filter(Boolean);
      if (data.cascade_applied && parts.length) {
        toast.success(`Concierge gewechselt · ${parts.join(" · ")} übertragen.`);
      } else {
        toast.success(next ? "Concierge zugewiesen." : "Concierge-Zuweisung entfernt.");
      }
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
      setValue(property.assigned_concierge_id || "");
    } finally {
      setSaving(false);
      setCascade(null);
    }
  };

  const save = async (next) => {
    const oldId = property.assigned_concierge_id || "";
    if (!oldId || oldId === next) {
      // Frische Zuweisung oder identisch → keine Cascade nötig, direkt speichern.
      setValue(next);
      await commit(next, false);
      return;
    }
    // Bestehender Concierge wird ersetzt/entfernt → erst Preview holen.
    // 23 Sep 2026 · Bugfix „Zuweisung wird nicht übernommen": Das Dropdown
    // wird NICHT mehr optimistisch auf `next` gesetzt, solange der Cascade-
    // Dialog noch offen ist. Sonst sah die Auswahl gespeichert aus, obwohl der
    // Nutzer den Dialog nie bestätigt hatte → Objektübersicht zeigte den alten
    // Concierge. Der `value` bleibt bis zum echten Commit auf dem alten Wert.
    setSaving(true);
    try {
      const { data: preview } = await api.post(
        `/properties/${property.id}/reassign-concierge-preview`,
        { new_concierge_id: next || "" },
      );
      if ((preview?.total_open_items || 0) === 0) {
        setValue(next);
        await commit(next, false);
      } else {
        setCascade({
          preview,
          next,
          oldName: nameOf(oldId),
          newName: next ? nameOf(next) : "— (keine Zuweisung)",
        });
        setSaving(false);
      }
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
      setValue(property.assigned_concierge_id || "");
      setSaving(false);
    }
  };

  return (
    <div
      data-testid={`property-concierge-assign-${property.id}`}
      className="bg-white border border-navy/10 px-4 py-3 flex items-center gap-3 flex-wrap"
      style={value ? { borderColor: withAlpha(BRAND_COLORS.gold, 0.4) } : undefined}
    >
      <div
        className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
        style={{ background: withAlpha(BRAND_COLORS.navy, 0.08), color: BRAND_COLORS.navy }}
      >
        <UserCheck className="w-4 h-4" strokeWidth={1.6} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="label-caps text-[10.5px] text-navy-dark/60 mb-0.5">
          Verantwortlicher Concierge · {property.name}
        </div>
        {concierges === null ? (
          <div className="flex items-center gap-2 text-[12px] text-navy-dark/50 italic">
            <Loader2 className="w-3 h-3 animate-spin" strokeWidth={1.6} /> Lade Team …
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <select
              value={value}
              onChange={(e) => save(e.target.value)}
              disabled={saving || concierges.length === 0}
              data-testid={`property-concierge-select-${property.id}`}
              className="bg-white border border-navy/20 focus:border-gold focus:outline-none px-3 py-1.5 text-[13px] text-navy-dark min-w-[220px] disabled:opacity-50"
            >
              <option value="">— Kein Concierge zugewiesen —</option>
              {concierges.map((c) => (
                <option key={c.user_id} value={c.user_id}>
                  {c.name}{c.role === "admin" ? " · Admin" : ""}
                </option>
              ))}
            </select>
            {saving && <Loader2 className="w-4 h-4 animate-spin text-gold" strokeWidth={1.6} />}
            {concierges.length === 0 && (
              <span className="text-[11.5px] text-navy-dark/50 italic">
                Kein Team-Mitglied eingeloggt.
              </span>
            )}
          </div>
        )}
      </div>
      {cascade && (
        <CascadeConfirmModal
          data={cascade}
          onCancel={() => {
            setValue(property.assigned_concierge_id || "");
            setCascade(null);
          }}
          onKeep={() => commit(cascade.next, false)}
          onTransfer={() => commit(cascade.next, true)}
          saving={saving}
        />
      )}
    </div>
  );
}

function CascadeConfirmModal({ data, onCancel, onKeep, onTransfer, saving }) {
  const p = data.preview;
  const rows = [
    { label: "Offene Buchungen", n: p.open_bookings },
    { label: "Offene Aufgaben", n: p.open_requests },
    { label: "Offene Projekte", n: p.open_projects },
    { label: "Offene Termine", n: p.open_calendar_events },
  ].filter((r) => r.n > 0);
  return (
    <div className="fixed inset-0 z-[70] bg-navy/60 backdrop-blur-sm flex items-center justify-center p-4"
         data-testid="concierge-cascade-modal">
      <div className="bg-ivory max-w-[520px] w-full border border-navy/15 shadow-xl">
        <div className="px-6 py-5 border-b border-navy/10 flex items-center gap-3">
          <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
               style={{ background: withAlpha(STATUS_COLORS.blue, 0.12), color: STATUS_COLORS.blue }}>
            <ArrowRightLeft className="w-4 h-4" strokeWidth={1.7} />
          </div>
          <div>
            <div className="label-caps text-[10px] text-navy-dark/60">Concierge-Wechsel</div>
            <div className="font-display text-lg text-navy">Auch offene Items übertragen?</div>
          </div>
        </div>
        <div className="px-6 py-5 space-y-3 text-[13px] text-navy-dark/85">
          <p>
            Der bisherige Verantwortliche <strong>{data.oldName}</strong> hat aktuell noch
            offene Items an diesem Objekt. Sollen sie automatisch auf{" "}
            <strong>{data.newName}</strong> übertragen werden?
          </p>
          <ul className="border border-navy/10 divide-y divide-navy/10">
            {rows.map((r) => (
              <li key={r.label} className="px-3 py-2 flex items-center justify-between text-[12.5px]">
                <span>{r.label}</span>
                <span className="font-mono tabular-nums text-navy" data-testid={`cascade-count-${r.label}`}>
                  {r.n}
                </span>
              </li>
            ))}
          </ul>
          <p className="text-[11.5px] text-navy-dark/60 leading-snug">
            „Beim {data.oldName ? "Alten" : ""} lassen" bedeutet: {data.oldName} bleibt weiter
            Item-Verantwortlicher und sieht sie in seiner Ansicht.
          </p>
        </div>
        <div className="px-6 py-4 border-t border-navy/10 flex items-center justify-end gap-2 flex-wrap">
          <button type="button" onClick={onCancel} disabled={saving}
                  data-testid="cascade-cancel-btn"
                  className="text-[12px] tracking-wider uppercase text-navy-dark/60 hover:text-navy px-3 py-2">
            Abbrechen
          </button>
          <button type="button" onClick={onKeep} disabled={saving}
                  data-testid="cascade-keep-btn"
                  className="text-[12px] tracking-wider uppercase border border-navy/25 hover:bg-navy/5 px-3 py-2">
            Beim Alten lassen
          </button>
          <button type="button" onClick={onTransfer} disabled={saving}
                  data-testid="cascade-transfer-btn"
                  style={{ background: BRAND_COLORS.navy }}
                  className="text-[12px] tracking-wider uppercase text-ivory hover:opacity-90 disabled:opacity-60 px-4 py-2 inline-flex items-center gap-1.5">
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Übertragen
          </button>
        </div>
      </div>
    </div>
  );
}
